import { useId } from 'react';
import type { Project, TaskId } from '@bigint-project/core';

import {
  buildNetworkDiagramProjection,
  MAX_NETWORK_DIAGRAM_EDGES,
  MAX_NETWORK_DIAGRAM_NODES,
} from './projection.js';

export interface NetworkDiagramProps {
  readonly project: Pick<Project, 'tasks' | 'dependencies'>;
  readonly selectedTaskIds: readonly string[];
  readonly onSelectTask: (taskId: TaskId) => void;
}

const NODE_WIDTH = 220;
const NODE_HEIGHT = 92;
const GAP_X = 80;
const GAP_Y = 24;

export function NetworkDiagram({ project, selectedTaskIds, onSelectTask }: NetworkDiagramProps): React.JSX.Element {
  const arrowId = useId().replaceAll(':', '');
  const projection = buildNetworkDiagramProjection(project);
  if (projection.error !== null) {
    return <p role="status" className="p-4 text-sm text-slate-600 dark:text-slate-300">{projection.error}</p>;
  }
  if (projection.nodes.length === 0) {
    return <p role="status" className="p-4 text-sm text-slate-600 dark:text-slate-300">尚無任務可顯示。</p>;
  }

  const selected = new Set(selectedTaskIds.map(String));
  const maxLayer = Math.max(...projection.nodes.map((node) => node.layer));
  const maxRow = Math.max(...projection.nodes.map((node) => node.row));
  const width = 32 + (maxLayer + 1) * (NODE_WIDTH + GAP_X);
  const height = 32 + (maxRow + 1) * (NODE_HEIGHT + GAP_Y);
  const byId = new Map(projection.nodes.map((node) => [node.task.id as string, node]));
  const point = (node: { layer: number; row: number }) => ({
    x: 16 + node.layer * (NODE_WIDTH + GAP_X),
    y: 16 + node.row * (NODE_HEIGHT + GAP_Y),
  });

  return (
    <div
      data-testid="network-diagram"
      role="region"
      className="h-full w-full overflow-auto"
      aria-label={`任務網路圖，共 ${projection.nodes.length} 項任務`}
    >
      <div className="relative" style={{ width, height }}>
        <svg aria-hidden="true" className="pointer-events-none absolute inset-0" width={width} height={height}>
          <defs>
            <marker id={arrowId} markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
              <path d="M0,0 L8,4 L0,8 z" />
            </marker>
          </defs>
          {projection.edges.map((edge) => {
            const from = byId.get(edge.predecessorId as string)!;
            const to = byId.get(edge.successorId as string)!;
            const start = point(from);
            const end = point(to);
            const startX = start.x + NODE_WIDTH;
            const startY = start.y + NODE_HEIGHT / 2;
            const endX = end.x;
            const endY = end.y + NODE_HEIGHT / 2;
            return (
              <g key={edge.id as string} data-dependency-type={edge.type}>
                <path
                  d={`M ${startX} ${startY} L ${endX} ${endY}`}
                  stroke="currentColor"
                  fill="none"
                  markerEnd={`url(#${arrowId})`}
                  opacity="0.55"
                />
                <text
                  x={(startX + endX) / 2}
                  y={(startY + endY) / 2 - 4}
                  textAnchor="middle"
                  className="fill-slate-600 text-[10px] dark:fill-slate-300"
                >
                  {edge.type}
                </text>
              </g>
            );
          })}
        </svg>
        <ul className="sr-only" aria-label="任務相依清單">
          {projection.edges.map((edge) => (
            <li key={edge.id as string}>
              {byId.get(edge.predecessorId as string)!.task.name} 到 {byId.get(edge.successorId as string)!.task.name}，{edge.type}
            </li>
          ))}
        </ul>
        {projection.nodes.map((node) => {
          const position = point(node);
          const { task } = node;
          const isSelected = selected.has(task.id as string);
          const label = `${task.wbs} ${task.name}，開始 ${task.start}，完成 ${task.finish}，總浮時 ${task.totalFloat as number} 分鐘${task.isCritical ? '，要徑任務' : ''}`;
          return (
            <button
              key={task.id as string}
              type="button"
              aria-label={label}
              aria-pressed={isSelected}
              onClick={() => onSelectTask(task.id)}
              style={{ left: position.x, top: position.y, width: NODE_WIDTH, height: NODE_HEIGHT }}
              className={`absolute rounded border p-2 text-left text-xs ${
                task.isCritical
                  ? 'border-red-600 bg-red-50 dark:bg-red-950'
                  : 'border-slate-400 bg-white dark:bg-slate-900'
              } ${isSelected ? 'ring-2 ring-blue-600' : ''}`}
            >
              <strong>{task.wbs} {task.name}</strong>
              <br />開始：{task.start}
              <br />完成：{task.finish}
              <br />總浮時：{task.totalFloat as number} 分鐘
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { MAX_NETWORK_DIAGRAM_EDGES, MAX_NETWORK_DIAGRAM_NODES };
